import React from "react";

import moment from "moment";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { DefaultLoader, constant } from "@rdcs/dap-front-library";

import BloodSugarHeaderContent from "./BloodSugarHeaderContent";

import { tryFetchLastDiagnosis } from "../../../actions/diagnosis";
import { tryFetchMeasures } from "../../../actions/measures";
import { trySinglePatient } from "../../../actions/patients";

const headerLoadPath = ["dashboard"];

const BloodSugarHeader = ({
    measures,
    singlePatient,
    treatmentGroup,
    tryFetchLastDiagnosis,
    tryFetchMeasures,
    trySinglePatient,
}) => {
    const { id } = useParams();
    const { pathname } = useLocation();
    const patientId = React.useMemo(() => parseInt(id), [id]);
    const loadHeaderDatas = React.useMemo(() => {
        const paths = pathname.split("/");

        return headerLoadPath.includes(paths[paths.length - 1].toLowerCase());
    });
    const headerDatasIsLoaded = React.useMemo(
        () => Boolean(singlePatient && treatmentGroup),
        [singlePatient, treatmentGroup]
    );

    const [measuresLoading, setMeasuresLoading] = React.useState(true);
    const [lastDiagnosisLoading, setLastDiagnosisLoading] = React.useState(true);

    const patientHbA1c = React.useMemo(
        () => measures.filter((measure) => measure.type.name === "HbA1c").sort((a, b) => (a.date > b.date ? -1 : 1))[0],
        [measures]
    );

    const asgGoalSection = React.useMemo(
        () =>
            lastDiagnosisLoading || !singlePatient ? (
                <DefaultLoader />
            ) : (
                <>
                    <div className="title-2">
                        {treatmentGroup?.asgGoal && treatmentGroup.asgGoalUnit
                            ? `${treatmentGroup.asgGoal}/${constant("asgGoalUnits", treatmentGroup.asgGoalUnit)}`
                            : "-"}
                    </div>
                    <div className="dashboardAction__daysAgo">
                        <span className="dashboardAction__percentage mr-1">
                            {!singlePatient.bgmAverageDate || !singlePatient.bgmAverage /*||
                            moment().diff(moment(singlePatient.bgmAverageDate, "h")) > 48*/
                                ? "-"
                                : singlePatient.bgmAverage.toFixed(2)}
                            /j
                        </span>
                        {singlePatient.bgmAverageDate ? moment(singlePatient.bgmAverageDate).fromNow() : "Il y a -j"}
                    </div>
                </>
            ),
        [lastDiagnosisLoading, loadHeaderDatas, singlePatient, treatmentGroup]
    );

    React.useEffect(() => {
        if (loadHeaderDatas) {
            tryFetchMeasures(patientId).finally(() => setMeasuresLoading(false));
            tryFetchLastDiagnosis(patientId).finally(() => setLastDiagnosisLoading(false));
            trySinglePatient(patientId);
        } else if (headerDatasIsLoaded) {
            setMeasuresLoading(false);
            setLastDiagnosisLoading(false);
        }
    }, []);

    return (
        Boolean(loadHeaderDatas || (singlePatient && treatmentGroup)) && (
            <div className="bloodSugarHeader dashboardHeader">
                <BloodSugarHeaderContent
                    title={
                        <>
                            Objectif <span className="text-transform-none">HbA1c</span>
                        </>
                    }
                >
                    {measuresLoading ? (
                        <DefaultLoader />
                    ) : (
                        <>
                            <div className="title-2">
                                {singlePatient?.lastDiagnostic ? (
                                    <span>{singlePatient.lastDiagnostic.goal.toFixed(1)}</span>
                                ) : (
                                    "-"
                                )}
                                %
                            </div>
                            <div className="dashboardAction__daysAgo">
                                <span className="dashboardAction__percentage mr-1">
                                    {patientHbA1c ? parseFloat(patientHbA1c.value).toFixed(1) : "-"}%
                                </span>
                                {patientHbA1c ? moment(patientHbA1c.date).fromNow() : "Il y a -j"}
                            </div>
                        </>
                    )}
                </BloodSugarHeaderContent>
                <BloodSugarHeaderContent title="Objectif ASG" hasSeparator={false}>
                    {asgGoalSection}
                </BloodSugarHeaderContent>
            </div>
        )
    );
};

BloodSugarHeader.propTypes = {
    measures: PropTypes.arrayOf(PropTypes.object).isRequired,
    singlePatient: PropTypes.object,
    treatmentGroup: PropTypes.object,
    tryFetchLastDiagnosis: PropTypes.func.isRequired,
    tryFetchMeasures: PropTypes.func.isRequired,
    trySinglePatient: PropTypes.func.isRequired,
};

const mapStateToProps = ({
    patients: { singlePatient },
    diagnosis: { patientLastDiagnosis },
    measures: { list: measures },
}) => ({
    singlePatient,
    treatmentGroup: patientLastDiagnosis?.treatmentGroup,
    measures,
});

const mapDispatchToProps = {
    tryFetchLastDiagnosis,
    trySinglePatient,
    tryFetchMeasures,
};

export default React.memo(connect(mapStateToProps, mapDispatchToProps)(BloodSugarHeader));
